import {
  parseCollectionVersionRef,
  parseDocumentVersionRef,
  type CollectionVersionRef,
  type DocumentVersionRef,
} from "../../shared/references/contentReferences";
import {
  JsonContractError,
  readArray,
  readInteger,
  readNullableString,
  readObject,
  readString,
  type JsonObject,
} from "../http/jsonDecoding";
import type { SegmentNode } from "./documentSegments";

/**
 * Decoders for the content routes' bodies (`server/src/dmw/api/routes/content/bodies.py`).
 * References arrive in their text forms and are split here, so the views get typed parts.
 */

export function readDocumentVersionRef(object: JsonObject, member: string): DocumentVersionRef {
  const reference = parseDocumentVersionRef(readString(object, member));
  if (reference === null) {
    throw new JsonContractError(`Expected "${member}" to be a document version reference.`);
  }
  return reference;
}

export function readCollectionVersionRef(object: JsonObject, member: string): CollectionVersionRef {
  const reference = parseCollectionVersionRef(readString(object, member));
  if (reference === null) {
    throw new JsonContractError(`Expected "${member}" to be a collection version reference.`);
  }
  return reference;
}

/** One version of a collection: a model version or a corpus version. */
export interface CollectionVersionSummary {
  /** `CollectionVersionRef` `<collection id>@<commit>`. */
  readonly reference: string;
  readonly commit: string;
  readonly message: string;
  /** ISO 8601. */
  readonly committedAt: string;
  readonly author: string | null;
}

export function readCollectionVersionSummary(value: unknown, what: string): CollectionVersionSummary {
  const version = readObject(value, what);
  return {
    reference: readString(version, "reference"),
    commit: readString(version, "commit"),
    message: readString(version, "message"),
    committedAt: readString(version, "committed_at"),
    author: readNullableString(version, "author"),
  };
}

export function readSegmentTree(value: unknown): SegmentNode {
  const segment = readObject(value, "segment");
  return {
    segmentType: readString(segment, "segment_type"),
    path: readString(segment, "path"),
    start: readInteger(segment, "start"),
    end: readInteger(segment, "end"),
    children: readArray(segment["children"], "segment children", readSegmentTree),
  };
}

export function readStringList(object: JsonObject, member: string): string[] {
  return readArray(object[member], `"${member}"`, (item) => readString({ item }, "item"));
}
